import {
  ID,
  ListApiResponse,
  SingleApiResponse,
  SuccessApiResponse,
  unwrapApiErrorDetails
} from '@ianesthesie-kit/services';
import { doctorBffApi } from '@/modules';
import {
  DirectPatientRequest,
  PendingAppointment,
  PendingPatientActivationRequest,
  PendingPatientRequest
} from '@/modules/pendingPatient/pendingPatient.model';
import { PendingPatientService } from '@/modules/pendingPatient/pendingPatient.service';
import { Patient } from '@/modules/surgicalEvent';

export const pendingPatientApi = doctorBffApi.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    pendingAppointments: builder.query<
      ListApiResponse<PendingAppointment>,
      Parameters<typeof PendingPatientService.fetchPendingAppointments>[0]
    >({
      providesTags: ['PendingPatients'],
      queryFn: (params) =>
        PendingPatientService.fetchPendingAppointments(params)
          .then((data) => ({
            data
          }))
          .catch((error) => ({
            error
          }))
    }),

    createPendingPatient: builder.mutation<
      SuccessApiResponse,
      PendingPatientRequest
    >({
      invalidatesTags: ['PendingPatients'],
      queryFn: (payload) =>
        PendingPatientService.createPendingPatient(payload)
          .then((data) => ({
            data
          }))
          .catch((error) => ({
            error
          }))
    }),

    activatePendingPatient: builder.mutation<
      Patient,
      PendingPatientActivationRequest
    >({
      invalidatesTags: (result, error) => {
        const bc = error && unwrapApiErrorDetails(error)?.businessCode;
        const isPatientAlreadyExistsError = bc?.startsWith('E1-1-2-1012');
        return error && !isPatientAlreadyExistsError
          ? []
          : ['PendingPatients', 'Dashboard'];
      },
      queryFn: (payload) =>
        PendingPatientService.activatePendingPatient(payload)
          .then((data) => ({
            data: data.data
          }))
          .catch((error) => ({
            error
          }))
    }),

    createDirectPatient: builder.mutation<
      SingleApiResponse<Patient>,
      DirectPatientRequest
    >({
      invalidatesTags: ['Dashboard'],
      queryFn: (payload) =>
        PendingPatientService.createDirectPatient(payload)
          .then((resp) => ({
            data: resp
          }))
          .catch((error) => ({
            error
          }))
    }),

    deletePendingAppointment: builder.mutation<SuccessApiResponse, ID>({
      invalidatesTags: ['PendingPatients', 'Dashboard'],
      queryFn: (id) =>
        PendingPatientService.deletePendingAppointment(id)
          .then((data) => ({
            data
          }))
          .catch((error) => ({
            error
          }))
    })
  })
});

export const {
  useCreateDirectPatientMutation,
  useCreatePendingPatientMutation,
  useActivatePendingPatientMutation,
  useDeletePendingAppointmentMutation,
  usePendingAppointmentsQuery
} = pendingPatientApi;
